import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '../utils/AuthContext';
import DateTimePicker from '../components/DateTimePicker';
import config from '../config';
import './CreateEventPage.css';

const EditEventPage = () => {
  const [formData, setFormData] = useState({
    title: '',
    event_date: '',
    start_time: '13:00', // Default to 1:00 PM
    end_time: '14:00', // Default to 2:00 PM
    end_date: '',
    location: '',
    description: '',
    link: '',
    is_recurring: false,
    recurrence_type: 'weekly',
    recurrence_end_date: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [eventLoading, setEventLoading] = useState(true);
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();

  const fetchEvent = useCallback(async () => {
    try {
      const response = await fetch(`${config.API_BASE_URL}/api/events/${id}`);
      if (response.ok) {
        const event = await response.json();
        
        // Check if user owns this event
        if (event.created_by !== user.id) {
          setError('You can only edit your own events');
          return;
        }
        
        // Convert database format to form format
        // Extract recurrence type from rule if present
        let recurrenceType = 'weekly';
        if (event.recurrence_rule) {
          if (event.recurrence_rule.includes('DAILY')) recurrenceType = 'daily';
          else if (event.recurrence_rule.includes('WEEKLY;INTERVAL=2')) recurrenceType = 'biweekly';
          else if (event.recurrence_rule.includes('WEEKLY')) recurrenceType = 'weekly';
          else if (event.recurrence_rule.includes('MONTHLY')) recurrenceType = 'monthly';
        }
        
        setFormData({
          title: event.title,
          event_date: event.event_date.split('T')[0], // Convert to YYYY-MM-DD
          start_time: event.start_time.slice(0, 5), // Convert to HH:MM
          end_time: event.end_time ? event.end_time.slice(0, 5) : '14:00',
          end_date: event.end_date ? event.end_date.split('T')[0] : '',
          location: event.location,
          description: event.description || '',
          link: event.link || '',
          is_all_day: event.is_all_day || false,
          is_recurring: event.is_recurring || false,
          recurrence_type: recurrenceType,
          recurrence_end_date: event.recurrence_end_date ? event.recurrence_end_date.split('T')[0] : ''
        });
      } else {
        setError('Event not found');
      }
    } catch (error) {
      setError('Failed to load event');
    } finally {
      setEventLoading(false);
    }
  }, [id, user]);

  useEffect(() => {
    // Wait for auth to finish loading before checking user
    if (authLoading) return;
    
    if (!user) {
      navigate('/login');
      return;
    }
    fetchEvent();
  }, [user, authLoading, navigate, id, fetchEvent]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: value
      };
      
      // If the start date changes and there's no end date, default end date to start date
      if (name === 'event_date' && !prev.end_date) {
        newData.end_date = value;
      }
      
      return newData;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Clean up form data - only send recurrence fields if is_recurring is true
    const dataToSend = {
      title: formData.title,
      event_date: formData.event_date,
      start_time: formData.is_all_day ? '00:00' : formData.start_time,
      end_time: formData.is_all_day ? '23:59' : (formData.end_time || undefined),
      end_date: formData.end_date || undefined,
      location: formData.location,
      description: formData.description || undefined,
      link: formData.link || undefined,
      is_all_day: formData.is_all_day,
      is_recurring: formData.is_recurring
    };
    
    // Only include recurrence fields if event is recurring
    if (formData.is_recurring) {
      dataToSend.recurrence_type = formData.recurrence_type;
      dataToSend.recurrence_end_date = formData.recurrence_end_date;
    }

    try {
      const response = await fetch(`${config.API_BASE_URL}/api/events/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(dataToSend),
        credentials: 'include'
      });

      const data = await response.json();

      if (response.ok) {
        navigate('/');
      } else {
        // Show error with suggestion if provided
        const errorMessage = data.suggestion 
          ? `${data.error}\n\nSuggestion: ${data.suggestion}`
          : (data.error || 'Failed to update event');
        setError(errorMessage);
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Show loading while auth is loading
  if (authLoading) {
    return (
      <div className="create-event-page">
        <div className="create-event-container">
          <div className="loading">Loading...</div>
        </div>
      </div>
    );
  }

  if (!user) {
    return null; // Will redirect to login
  }

  if (eventLoading) {
    return (
      <div className="create-event-page">
        <div className="create-event-container">
          <div className="loading">Loading event...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="create-event-page">
      <div className="create-event-container">
        <div className="create-event-header">
          <h2>Edit Event</h2>
          <p>Update your event details</p>
        </div>

        <form onSubmit={handleSubmit} className="create-event-form">
          {error && <div className="error-message">{error}</div>}
          
          <div className="form-group">
            <label htmlFor="title">Event Title *</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              disabled={loading}
              maxLength="255"
            />
          </div>

          <div className="form-group">
            <label htmlFor="is_all_day" style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="checkbox"
                id="is_all_day"
                name="is_all_day"
                checked={formData.is_all_day}
                onChange={(e) => setFormData(prev => ({ ...prev, is_all_day: e.target.checked }))}
                disabled={loading}
                style={{ marginRight: '8px', width: 'auto', minWidth: 'auto' }}
              />
              All day event
            </label>
          </div>

          <DateTimePicker
            dateValue={formData.event_date}
            timeValue={formData.start_time}
            onDateChange={(date) => setFormData(prev => {
              const newData = { ...prev, event_date: date };
              // If no end date is set, default it to the start date
              if (!prev.end_date) {
                newData.end_date = date;
              }
              return newData;
            })}
            onTimeChange={(time) => setFormData(prev => ({ ...prev, start_time: time }))}
            label="Event Start"
            required={true}
            disabled={loading}
            hideTime={formData.is_all_day}
          />

          <DateTimePicker
            dateValue={formData.end_date}
            timeValue={formData.end_time}
            onDateChange={(date) => setFormData(prev => ({ ...prev, end_date: date }))}
            onTimeChange={(time) => setFormData(prev => ({ ...prev, end_time: time }))}
            label="Event End"
            required={!formData.is_all_day}
            timeRequired={!formData.is_all_day}
            disabled={loading}
            hideTime={formData.is_all_day}
          />

          <div className="form-group">
            <label htmlFor="location">Location in Toronto *</label>
            <input
              type="text"
              id="location"
              name="location"
              value={formData.location}
              onChange={handleChange}
              required
              disabled={loading}
              maxLength="255"
              placeholder="e.g. CN Tower, 290 Bremner Blvd"
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              disabled={loading}
              maxLength="2000"
              rows="4"
              placeholder="Optional event description..."
            />
          </div>

          <div className="form-group">
            <label htmlFor="link">Event Link</label>
            <input
              type="url"
              id="link"
              name="link"
              value={formData.link}
              onChange={handleChange}
              disabled={loading}
              placeholder="https://example.com"
            />
            <small>Optional: Add a link for tickets, registration, or more info</small>
          </div>

          <div className="form-group">
            <label htmlFor="is_recurring" style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="checkbox"
                id="is_recurring"
                name="is_recurring"
                checked={formData.is_recurring}
                onChange={(e) => setFormData(prev => ({ ...prev, is_recurring: e.target.checked }))}
                disabled={loading}
                style={{ marginRight: '8px', width: 'auto', minWidth: 'auto' }}
              />
              Make this a recurring event
            </label>
          </div>

          {formData.is_recurring && (
            <>
              <div className="form-group">
                <label htmlFor="recurrence_type">Repeat</label>
                <select
                  id="recurrence_type"
                  name="recurrence_type"
                  value={formData.recurrence_type}
                  onChange={handleChange}
                  disabled={loading}
                  required={formData.is_recurring}
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="biweekly">Biweekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="recurrence_end_date">End Recurring On</label>
                <input
                  type="date"
                  id="recurrence_end_date"
                  name="recurrence_end_date"
                  value={formData.recurrence_end_date}
                  onChange={handleChange}
                  disabled={loading}
                  min={formData.event_date}
                  required={formData.is_recurring}
                />
                <small>The last date this event will occur</small>
              </div>
            </>
          )}

          <div className="form-actions">
            <Link to="/" className="btn btn-secondary">Cancel</Link>
            <button 
              type="submit" 
              className="btn btn-create-event"
              disabled={loading}
            >
              {loading ? 'Updating Event...' : 'Update Event'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditEventPage;